import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Dialog, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Lightning, UploadSimple, FileText, Certificate, Check, X, Trash, ArrowClockwise, Warning, PencilSimple, DownloadSimple } from "@phosphor-icons/react";
import { ScoreRadar, ScoreBars, IndicesBadges } from "@/components/ScoreCharts";
import UnifiedReportView from "@/components/UnifiedReportView";
import OutcomePanel from "@/components/OutcomePanel";
import { useAuth } from "@/contexts/AuthContext";
import ResizableDialogContent from "@/components/ResizableDialog";
import ConfirmDelete from "@/components/ConfirmDelete";
import EmployeeCertifications from "@/components/EmployeeCertifications";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatDateIT(iso) {
  if (!iso) return "";
  const p = iso.split("-");
  if (p.length === 3 && p[0].length === 4) return `${p[2]}-${p[1]}-${p[0]}`;
  return iso;
}

const TASK_STATUS_STYLES = {
  "Da iniziare": "bg-gray-100 text-gray-600",
  "In corso": "bg-blue-50 text-blue-700",
  "Sospesa": "bg-yellow-50 text-yellow-700",
  "Completata": "bg-green-50 text-green-700",
  "Da fare": "bg-gray-100 text-gray-600",
  "Completato": "bg-green-50 text-green-700",
};

function TaskCard({ task, empId, onUpdate }) {
  const [editing, setEditing] = useState(false);
  const [desc, setDesc] = useState(task.description);
  const [dueDate, setDueDate] = useState(task.due_date || "");
  const [priority, setPriority] = useState(task.priority);
  const currentStatus = task.status === "Da fare" ? "Da iniziare" : task.status === "Completato" ? "Completata" : (task.status || "Da iniziare");

  const save = async (field, value) => {
    try {
      await axios.put(`${API}/employees/${empId}/tasks/${task.id}`, { [field]: value }, { withCredentials: true });
      toast.success(field === "status" && value === "Completata" ? "Task completato e archiviato" : "Task aggiornato");
      onUpdate();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  const handleAction = async (action) => {
    try {
      const payload = action === "approve" ? { approved: true } : { status: "Rimosso" };
      await axios.put(`${API}/employees/${empId}/tasks/${task.id}`, payload, { withCredentials: true });
      toast.success(action === "approve" ? "Task approvato" : "Task rimosso");
      onUpdate();
    } catch { toast.error("Errore"); }
  };

  const isPending = task.is_auto && !task.approved;

  return (
    <div className={`p-4 rounded-xl border text-xs ${
      isPending ? 'border-[#F59E0B]/30 bg-[#F59E0B]/5' :
      currentStatus === 'Completata' ? 'border-[#10B981]/30 bg-[#10B981]/5' :
      currentStatus === 'Sospesa' ? 'border-[#F59E0B]/20 bg-[#F59E0B]/[0.03]' :
      currentStatus === 'In corso' ? 'border-[#1B4FD8]/20 bg-[#1B4FD8]/[0.03]' : 'border-[#0D1F3C]/8'}`}
      data-testid={`task-card-${task.id}`}>
      {/* Header: Priority + Category + Date */}
      <div className="flex items-center justify-between mb-2 gap-2">
        <div className="flex items-center gap-2 flex-wrap">
          <Select value={priority} onValueChange={(v) => { setPriority(v); save("priority", v); }}>
            <SelectTrigger className={`h-6 w-[80px] text-[10px] border-0 px-2 ${
              priority === 'Alta' ? 'bg-red-50 text-red-600' : priority === 'Media' ? 'bg-yellow-50 text-yellow-700' : 'bg-gray-100 text-gray-600'}`}
              data-testid={`priority-select-${task.id}`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="Alta">Alta</SelectItem>
              <SelectItem value="Media">Media</SelectItem>
              <SelectItem value="Bassa">Bassa</SelectItem>
            </SelectContent>
          </Select>
          <Badge variant="outline" className="text-[10px]">{task.category}</Badge>
          {isPending && <Badge className="bg-[#F59E0B]/10 text-[#F59E0B] text-[10px]">Da approvare</Badge>}
        </div>
        <Input type="date" className="h-6 w-[130px] text-[10px] border-[#0D1F3C]/15 px-2"
          value={dueDate}
          onChange={(e) => setDueDate(e.target.value)}
          onBlur={() => { if (dueDate !== task.due_date) save("due_date", dueDate); }}
          data-testid={`date-input-${task.id}`} />
      </div>

      {/* Description: editable */}
      {editing ? (
        <Textarea className="text-xs text-[#0D1F3C]/80 leading-relaxed min-h-[60px] mb-2"
          value={desc} onChange={(e) => setDesc(e.target.value)}
          onBlur={() => { if (desc !== task.description) save("description", desc); setEditing(false); }}
          onKeyDown={(e) => { if (e.key === "Escape") { setDesc(task.description); setEditing(false); }}}
          autoFocus data-testid={`desc-textarea-${task.id}`} />
      ) : (
        <p className="text-[#0D1F3C]/80 leading-relaxed cursor-text hover:bg-[#0D1F3C]/[0.03] rounded px-1 -mx-1 py-1 transition-colors"
          onClick={() => setEditing(true)} title="Clicca per modificare"
          data-testid={`desc-text-${task.id}`}>
          {task.description}
        </p>
      )}

      {/* Actions: always visible */}
      <div className="flex gap-2 mt-3 border-t border-[#0D1F3C]/5 pt-2 items-center">
        {isPending ? (
          <Button size="sm" variant="outline" className="h-6 text-[10px] border-[#10B981]/30 text-[#10B981]"
            onClick={() => handleAction("approve")} data-testid={`approve-btn-${task.id}`}>
            <Check size={12} className="mr-1" />Approva
          </Button>
        ) : (
          <Select value={currentStatus} onValueChange={(v) => save("status", v)}>
            <SelectTrigger className={`h-6 w-[110px] text-[10px] border-0 px-2 ${TASK_STATUS_STYLES[currentStatus] || 'bg-gray-100 text-gray-600'}`}
              data-testid={`status-select-${task.id}`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="Da iniziare">Da iniziare</SelectItem>
              <SelectItem value="In corso">In corso</SelectItem>
              <SelectItem value="Sospesa">Sospesa</SelectItem>
              <SelectItem value="Completata">Completata</SelectItem>
            </SelectContent>
          </Select>
        )}
        <ConfirmDelete title="Elimina task" description="Sei sicuro di voler eliminare questo task? Questa azione non puo essere annullata." onConfirm={() => handleAction("remove")}>
          <Button size="sm" variant="outline" className="h-6 text-[10px] border-red-200 text-red-400 hover:text-red-600 hover:bg-red-50 ml-auto"
            data-testid={`remove-btn-${task.id}`}>
            <Trash size={12} className="mr-1" />Elimina
          </Button>
        </ConfirmDelete>
      </div>
    </div>
  );
}

export default function EmployeeDetailDialog({ empId, open, onClose, onRefresh, onEdit }) {
  const { user } = useAuth();
  const isAdmin = !!user?.is_admin;
  const [emp, setEmp] = useState(null);
  const [regenerating, setRegenerating] = useState(false);
  const [loading, setLoading] = useState(false);
  const [certForm, setCertForm] = useState({ name: "", expiry_date: "", alert_days: 2, no_expiry: false });
  const [generating, setGenerating] = useState(false);
  const [report, setReport] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const [ensuringReport, setEnsuringReport] = useState(false);  // eslint-disable-line no-unused-vars
  // BUG-58: stato esplicito generazione report per loader UI
  const [reportStatus, setReportStatus] = useState("idle"); // idle | pending | generating | ready | failed | absent
  const [reportElapsed, setReportElapsed] = useState(0);
  const pollRef = useRef(null);
  const elapsedRef = useRef(null);

  // BUG-58: polling /report-status finché ready o failed
  const startReportPolling = (eid) => {
    if (pollRef.current) clearInterval(pollRef.current);
    if (elapsedRef.current) clearInterval(elapsedRef.current);
    setReportElapsed(0);
    elapsedRef.current = setInterval(() => setReportElapsed(t => t + 1), 1000);
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await axios.get(`${API}/employees/${eid}/report-status`, { withCredentials: true });
        setReportStatus(data.status);
        if (data.status === "ready" && data.report_id) {
          clearInterval(pollRef.current); pollRef.current = null;
          clearInterval(elapsedRef.current); elapsedRef.current = null;
          const r = await axios.get(`${API}/reports/${data.report_id}`, { withCredentials: true });
          setReport(r.data);
        } else if (data.status === "failed" || data.status === "absent") {
          clearInterval(pollRef.current); pollRef.current = null;
          clearInterval(elapsedRef.current); elapsedRef.current = null;
        }
      } catch (e) { /* keep polling */ }
    }, 5000);
  };

  useEffect(() => () => {
    if (pollRef.current) clearInterval(pollRef.current);
    if (elapsedRef.current) clearInterval(elapsedRef.current);
  }, []);

  const loadEmp = async () => {
    if (!empId) return;
    try {
      const r = await axios.get(`${API}/employees/${empId}`, { withCredentials: true });
      setEmp(r.data);
    } catch { toast.error("Errore caricamento dipendente"); return; }
    // BUG-58/59: usa /report-status come single source of truth
    try {
      const { data: st } = await axios.get(`${API}/employees/${empId}/report-status`, { withCredentials: true });
      setReportStatus(st.status);
      if (st.status === "ready" && st.report_id) {
        const r = await axios.get(`${API}/reports/${st.report_id}`, { withCredentials: true });
        setReport(r.data);
      } else if (st.status === "pending") {
        // Test completato ma report mai partito → kick async + polling
        setReport(null);
        try {
          await axios.post(`${API}/employees/${empId}/ensure-report`, {}, { withCredentials: true });
        } catch (e) { /* noop */ }
        setReportStatus("generating");
        startReportPolling(empId);
      } else if (st.status === "generating") {
        setReport(null);
        startReportPolling(empId);
      } else if (st.status === "failed") {
        setReport(null);
      } else {
        setReport(null);
      }
    } catch { /* noop */ }
  };

  useEffect(() => {
    if (empId && open) { setLoading(true); loadEmp(); setLoading(false); }
  }, [empId, open]);

  const refresh = () => { loadEmp(); if (onRefresh) onRefresh(); };

  const addCertification = async () => {
    try {
      await axios.post(`${API}/employees/${empId}/certifications`, certForm, { withCredentials: true });
      toast.success("Certificazione aggiunta");
      setCertForm({ name: "", expiry_date: "", alert_days: 2 });
      loadEmp();
    } catch { toast.error("Errore"); }
  };

  const tasks = (emp?.action_plan || []).filter(t => t.status !== "Rimosso");
  const proposte = tasks.filter(t => t.is_auto && !t.approved);
  const approvati = tasks.filter(t => !t.is_auto || t.approved);
  const genCount = emp?.action_plan_gen_count || 0;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <ResizableDialogContent defaultW={900} defaultH={700}>
        {loading && !emp ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin w-8 h-8 border-2 border-[#1B4FD8] border-t-transparent rounded-full" />
          </div>
        ) : emp ? (
          <>
            <DialogHeader>
              <div className="flex items-start justify-between gap-3">
                <DialogTitle className="text-lg">{emp.first_name} {emp.last_name}</DialogTitle>
                <div className="flex items-center gap-2">
                  {report?.id && (
                    <Button variant="outline" size="sm"
                      className="border-[#1B4FD8]/30 text-[#1B4FD8] hover:bg-[#1B4FD8]/5 gap-1.5 text-xs"
                      title="Scarica il report in PDF"
                      data-testid="emp-detail-pdf-btn"
                      onClick={async () => {
                        try {
                          const response = await axios.get(`${API}/reports/${report.id}/pdf`, { withCredentials: true, responseType: 'blob' });
                          const url = window.URL.createObjectURL(new Blob([response.data]));
                          const a = document.createElement('a'); a.href = url;
                          a.download = `report_${emp.first_name}_${emp.last_name}.pdf`;
                          document.body.appendChild(a); a.click(); a.remove();
                          window.URL.revokeObjectURL(url);
                          toast.success("PDF scaricato");
                        } catch { toast.error("Errore download PDF"); }
                      }}>
                      <DownloadSimple size={14} /> PDF
                    </Button>
                  )}
                  {onEdit && (
                    <Button variant="outline" size="sm" onClick={() => onEdit(emp)} data-testid="emp-detail-edit-btn"
                      className="border-[#1B4FD8]/30 text-[#1B4FD8] hover:bg-[#1B4FD8]/5 gap-1.5 text-xs">
                      <PencilSimple size={14} /> Modifica
                    </Button>
                  )}
                  <Button variant="outline" size="sm" onClick={() => setConfirmDelete(true)} data-testid="emp-detail-delete-btn"
                    className="border-red-500/30 text-red-600 hover:bg-red-50 gap-1.5 text-xs">
                    <Trash size={14} /> Elimina
                  </Button>
                </div>
              </div>
            </DialogHeader>
            <div className="space-y-5 text-sm mt-3">
              {/* Basic Info */}
              <div className="grid grid-cols-3 gap-3">
                <div><span className="text-[#0D1F3C]/50">Email:</span> {emp.email || '—'}</div>
                <div data-testid="emp-detail-phone"><span className="text-[#0D1F3C]/50">Telefono:</span> {emp.phone || '—'}</div>
                <div><span className="text-[#0D1F3C]/50">Ruolo:</span> {emp.job_title || '—'}</div>
                <div><span className="text-[#0D1F3C]/50">Reparto:</span> {emp.department || '—'}</div>
                <div><span className="text-[#0D1F3C]/50">Anzianita:</span> {emp.years_experience} anni</div>
                <div><span className="text-[#0D1F3C]/50">Tipo:</span> {emp.is_manager ? 'Responsabile' : 'Dipendente'}</div>
                <div><span className="text-[#0D1F3C]/50">Analisi:</span> <Badge className={`text-[10px] ${emp.test_status === 'Completato' ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-600'}`}>{emp.test_status}</Badge></div>
              </div>

              {/* CV */}
              <div className="flex items-center gap-3 p-3 rounded-lg border border-[#0D1F3C]/8 bg-[#F4F7FB]">
                {emp.cv_filename ? (
                  <div className="flex items-center gap-2 flex-1">
                    <FileText size={20} weight="duotone" className="text-[#1B4FD8]" />
                    <span className="text-sm text-[#0D1F3C]">{emp.cv_filename}</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 flex-1">
                    <UploadSimple size={20} className="text-[#0D1F3C]/30" />
                    <span className="text-sm text-[#0D1F3C]/50">Nessun CV caricato</span>
                  </div>
                )}
                <label className="cursor-pointer">
                  <input type="file" accept=".pdf,.docx,.doc" className="hidden"
                    onChange={async (e) => {
                      const file = e.target.files[0];
                      if (!file) return;
                      const fd = new FormData();
                      fd.append("file", file);
                      try {
                        await axios.post(`${API}/employees/${empId}/upload-cv`, fd, { withCredentials: true, headers: { "Content-Type": "multipart/form-data" }});
                        toast.success("CV caricato");
                        loadEmp();
                      } catch { toast.error("Errore upload CV"); }
                    }} />
                  <Button variant="outline" size="sm" asChild><span><UploadSimple size={14} className="mr-1" />Carica CV</span></Button>
                </label>
              </div>

              {/* FEATURE-04: Certificazioni e Scadenze */}
              <EmployeeCertifications employeeId={empId} />

              {/* Regeneration banner + button (Batch 14v2) — solo quando report ready */}
              {(emp.scores || report) && reportStatus !== "generating" && reportStatus !== "pending" && (
                <div className="flex items-center justify-between gap-2" data-testid="regenerate-row">
                  {emp.needs_refresh && !isAdmin ? (
                    <div className="flex-1 flex items-center gap-2 p-3 rounded-lg bg-amber-50 border border-amber-200" data-testid="needs-refresh-banner">
                      <Warning size={18} weight="fill" className="text-amber-700 flex-shrink-0" />
                      <span className="text-sm text-amber-900">
                        Mansionario o Note HR sono stati modificati dopo la generazione del report.
                      </span>
                    </div>
                  ) : <div />}
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {emp.needs_refresh && !isAdmin && (
                      <Button
                        size="sm"
                        onClick={async () => {
                          const qleft = user?.quota?.tests_limit >= 9999 ? "∞" : (user?.quota?.tests_limit - user?.quota?.tests_used);
                          if (!window.confirm(`Rigenerare il report consuma 1 analisi dalla tua quota (residue: ${qleft}). Continuare?`)) return;
                          setRegenerating(true);
                          try {
                            await axios.post(`${API}/employees/${empId}/regenerate-report`, {}, { withCredentials: true });
                            toast.success("Rigenerazione avviata, attendi 30-60 secondi");
                            setReport(null);
                            setReportStatus("generating");
                            startReportPolling(empId);
                          } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
                          finally { setRegenerating(false); }
                        }}
                        disabled={regenerating}
                        className="bg-amber-600 hover:bg-amber-700 text-white"
                        data-testid="needs-refresh-regenerate-btn"
                      >
                        <ArrowClockwise size={14} className="mr-1" />{regenerating ? "In corso..." : "Aggiorna Report"}
                      </Button>
                    )}
                    {isAdmin && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={async () => {
                          if (!window.confirm("Admin: forzare la rigenerazione con le STYLE_RULES attuali?")) return;
                          setRegenerating(true);
                          try {
                            await axios.post(`${API}/employees/${empId}/regenerate-report`, {}, { withCredentials: true });
                            toast.success("Rigenerazione avviata, attendi 30-60 secondi");
                            setReport(null);
                            setReportStatus("generating");
                            startReportPolling(empId);
                          } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
                          finally { setRegenerating(false); }
                        }}
                        disabled={regenerating}
                        className="border-[#1B4FD8] text-[#1B4FD8]"
                        data-testid="admin-regenerate-btn"
                        title="Admin: forza rigenerazione · non consuma quota"
                      >
                        <ArrowClockwise size={14} className="mr-1" />{regenerating ? "In corso..." : "Rigenera Report"}
                      </Button>
                    )}
                  </div>
                </div>
              )}

              {/* BUG-58: loader esplicito durante elaborazione report */}
              {(reportStatus === "generating" || reportStatus === "pending") && !report && emp.scores && (
                <div className="flex flex-col items-center justify-center gap-3 py-10 px-4 rounded-lg border border-[#1B4FD8]/20 bg-[#1B4FD8]/5"
                     data-testid="report-generating-loader">
                  <div className="animate-spin w-10 h-10 border-3 border-[#1B4FD8] border-t-transparent rounded-full" />
                  <h4 className="text-sm font-medium text-[#0D1F3C]">
                    Stiamo elaborando il report di {emp.first_name} {emp.last_name}.
                  </h4>
                  <p className="text-xs text-[#0D1F3C]/60 text-center max-w-md">
                    Operazione in corso, attendi 30-60 secondi. La pagina si aggiornerà automaticamente.
                  </p>
                  <p className="text-[10px] text-[#0D1F3C]/40 font-mono" data-testid="report-elapsed">
                    Tempo trascorso: {reportElapsed}s
                  </p>
                </div>
              )}

              {/* BUG-58: errore generazione + retry */}
              {reportStatus === "failed" && !report && emp.scores && (
                <div className="flex flex-col items-center justify-center gap-3 py-8 px-4 rounded-lg border border-red-200 bg-red-50"
                     data-testid="report-failed-banner">
                  <Warning size={28} weight="duotone" className="text-red-600" />
                  <h4 className="text-sm font-medium text-red-700">Elaborazione del report non riuscita</h4>
                  <p className="text-xs text-red-600/80 text-center max-w-md">
                    Si è verificato un errore durante l'elaborazione. Riprova oppure contatta il supporto.
                  </p>
                  <Button
                    size="sm"
                    onClick={async () => {
                      try {
                        await axios.post(`${API}/employees/${empId}/ensure-report`, {}, { withCredentials: true });
                        toast.success("Elaborazione riavviata");
                        setReportStatus("generating");
                        startReportPolling(empId);
                      } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
                    }}
                    className="bg-red-600 hover:bg-red-700 text-white"
                    data-testid="report-retry-btn"
                  >
                    <ArrowClockwise size={14} className="mr-1" /> Riprova elaborazione
                  </Button>
                </div>
              )}

              {/* Unified report view (charts inline + AI sections + single disclaimer + HR notes on top) */}
              {(emp.scores || report) && reportStatus !== "generating" && reportStatus !== "pending" && reportStatus !== "failed" && (
                <UnifiedReportView
                  report={report}
                  subject={emp}
                  entityType={emp.is_manager ? "manager" : "employee"}
                  entityId={empId}
                  onNotesUpdated={(newNotes) => setEmp({ ...emp, hr_notes: newNotes })}
                />
              )}

              {/* B76 — Outcome panel: cattura esito reale post-analisi (Fase 1 ciclo chiuso). */}
              <OutcomePanel
                subjectType="employee"
                subjectId={empId}
                hasScores={!!emp.scores}
                initialOutcome={emp.outcome || null}
              />

              {/* REFACTOR B: action plan operativo è ora SOLO in tab Schede.
                   Qui mostriamo solo un link informativo per gestione task. */}
              <div className="border-t border-[#0D1F3C]/8 pt-4">
                <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                  <h4 className="font-medium text-[#0D1F3C] flex items-center gap-2">
                    <Lightning size={18} weight="duotone" className="text-[#F59E0B]" />
                    Percorso di Sviluppo
                    <Badge variant="outline" className="text-[10px] font-normal">{tasks.length} task nel piano</Badge>
                  </h4>
                  <Button variant="outline" size="sm"
                    onClick={() => { window.location.href = `/employees?tab=schede&expand=${empId}`; }}
                    data-testid="goto-scheda-btn"
                    className="border-[#1B4FD8]/30 text-[#1B4FD8]">
                    Vai alla scheda dipendente per gestire le attività
                  </Button>
                </div>
                {tasks.length === 0 ? (
                  <p className="text-xs text-[#0D1F3C]/50 text-center py-4">
                    Nessuna attività nel piano di sviluppo. Vai alla <strong>scheda dipendente</strong> per generare il percorso o aggiungere attività manuali.
                  </p>
                ) : (
                  <p className="text-xs text-[#0D1F3C]/50 py-2">
                    Le attività operative sono visibili nella tab <strong>Schede</strong> della pagina Dipendenti. Qui sotto la sintesi statica per il report.
                  </p>
                )}
              </div>

              {/* Full Report already rendered by UnifiedReportView above */}
              {null && report?.html}

              {/* Certifications */}
              <div className="border-t border-[#0D1F3C]/8 pt-4">
                <h4 className="font-medium text-[#0D1F3C] mb-3 flex items-center gap-2">
                  <Certificate size={18} weight="duotone" className="text-[#1B4FD8]" />Certificazioni e Scadenze
                </h4>
                <div className="space-y-2 mb-3">
                  {(emp.certifications || []).map(cert => {
                    const daysLeft = cert.expiry_date ? Math.ceil((new Date(cert.expiry_date) - new Date()) / 86400000) : null;
                    const isExpiring = daysLeft !== null && daysLeft <= cert.alert_days;
                    const isExpired = daysLeft !== null && daysLeft < 0;
                    return (
                      <div key={cert.id} className={`p-3 rounded-lg border text-xs flex items-center justify-between ${
                        isExpired ? 'border-red-300 bg-red-50' : isExpiring ? 'border-[#F59E0B]/30 bg-[#F59E0B]/5' : 'border-[#0D1F3C]/8'}`}>
                        <div>
                          <span className="font-medium text-[#0D1F3C]">{cert.name}</span>
                          <span className="text-[#0D1F3C]/50 ml-2">{cert.expiry_date ? `Scadenza: ${formatDateIT(cert.expiry_date)}` : 'Senza scadenza'}</span>
                          {isExpired && <Badge className="ml-2 bg-red-100 text-red-600 text-[10px]">Scaduta</Badge>}
                          {isExpiring && !isExpired && <Badge className="ml-2 bg-[#F59E0B]/10 text-[#F59E0B] text-[10px]">Scade tra {daysLeft}gg</Badge>}
                        </div>
                        <ConfirmDelete title="Rimuovi certificazione" description={`Sei sicuro di voler rimuovere la certificazione "${cert.name}"?`}
                          onConfirm={async () => {
                            await axios.delete(`${API}/employees/${empId}/certifications/${cert.id}`, { withCredentials: true });
                            loadEmp();
                          }}>
                          <Button variant="ghost" size="sm" className="h-6 text-[10px] text-red-400">Rimuovi</Button>
                        </ConfirmDelete>
                      </div>
                    );
                  })}
                </div>
                <div className="flex gap-2 items-end flex-wrap">
                  <div className="flex-1 min-w-[140px]"><Label className="text-[10px] mb-0.5 block">Nome</Label><Input className="h-8 text-xs" value={certForm.name} onChange={e=>setCertForm(p=>({...p,name:e.target.value}))} placeholder="es. Sicurezza sul lavoro, IELTS" /></div>
                  <label className="flex items-center gap-1.5 pb-1 cursor-pointer">
                    <input type="checkbox" className="rounded border-[#0D1F3C]/20" checked={certForm.no_expiry} onChange={e=>setCertForm(p=>({...p,no_expiry:e.target.checked,expiry_date:e.target.checked?"":p.expiry_date}))} />
                    <span className="text-[10px] text-[#0D1F3C]/50">Senza scadenza</span>
                  </label>
                  {!certForm.no_expiry && (
                    <>
                      <div className="w-32"><Label className="text-[10px] mb-0.5 block">Scadenza</Label><Input className="h-8 text-xs" type="date" value={certForm.expiry_date} onChange={e=>setCertForm(p=>({...p,expiry_date:e.target.value}))} /></div>
                      <div className="w-16"><Label className="text-[10px] mb-0.5 block">Alert gg</Label><Input className="h-8 text-xs" type="number" value={certForm.alert_days} onChange={e=>setCertForm(p=>({...p,alert_days:parseInt(e.target.value)||2}))} /></div>
                    </>
                  )}
                  <Button size="sm" className="h-8 bg-[#1B4FD8] hover:bg-[#0D1F3C] text-xs" onClick={addCertification} disabled={!certForm.name||(!certForm.no_expiry&&!certForm.expiry_date)}>Aggiungi</Button>
                </div>
              </div>
              {/* Disclaimer rendered by UnifiedReportView above; removed duplicate */}
            </div>
          </>
        ) : null}
      </ResizableDialogContent>

      {/* BUG-27: conferma delete dipendente */}
      {confirmDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => !deleting && setConfirmDelete(false)}>
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start gap-3 mb-4">
              <Warning size={24} weight="duotone" className="text-red-500 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="text-base font-semibold text-[#0D1F3C] mb-1">Eliminare il dipendente?</h3>
                <p className="text-xs text-[#0D1F3C]/70">
                  <strong>{emp?.first_name} {emp?.last_name}</strong> verrà spostato nel cestino e cancellato definitivamente dopo <strong>30 giorni</strong>.
                  Le analisi e i report rimangono nello storico HR (no cascade).
                </p>
              </div>
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setConfirmDelete(false)} disabled={deleting} className="border-[#0D1F3C]/15">Annulla</Button>
              <Button onClick={async () => {
                setDeleting(true);
                try {
                  await axios.delete(`${API}/employees/${empId}`, { withCredentials: true });
                  toast.success("Dipendente spostato nel cestino");
                  setConfirmDelete(false);
                  if (onRefresh) onRefresh();
                  onClose();
                } catch (e) { toast.error(e?.response?.data?.detail || "Errore eliminazione"); }
                finally { setDeleting(false); }
              }} disabled={deleting} className="bg-red-600 hover:bg-red-700 text-white" data-testid="emp-delete-confirm-btn">
                {deleting ? "Eliminazione..." : "Sposta nel Cestino"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </Dialog>
  );
}
